// Fábrica compartida por 054 y 055 (Anexo 1 MSP) — tabla simple de
// resultados (nombre + valor + unidad + referencia, filas dinámicas), sin
// crear ningún recurso FHIR propio: son un resumen interno imprimible, no
// una orden ni un informe formal como 010B/012B/013B. Mismo criterio de
// fábrica parametrizada que ya usa `ordenDiagnostica.tsx` para 010A/B,
// 012A/B, 013A/B — acá los dos códigos comparten TODO el comportamiento,
// solo cambia el código y el título.
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { FormularioDiscretoProps, Par } from './index';
import { EncabezadoMspCampos, valoresInicialesEncabezadoMsp } from '../EncabezadoMspCampos';
import { citaHcu2008 } from '@logiacare/hce-pdf';
import { useDatosEncabezadoMsp } from '../useDatosEncabezadoMsp';

interface FilaResumenMsp {
  nombre: string;
  valor: string;
  unidad: string;
  referencia: string;
}

const FILA_VACIA: FilaResumenMsp = { nombre: '', valor: '', unidad: '', referencia: '' };

export interface FormularioResumenMspConfig {
  readonly codigo: string;
  /** Prefijo `formularios.<claveI18n>` para el título y las etiquetas de la tabla. */
  readonly claveI18n: string;
}

export function crearFormularioResumenMsp(config: FormularioResumenMspConfig) {
  return function FormularioResumenMsp({ subject, onGuardado }: FormularioDiscretoProps): JSX.Element {
    const { t } = useTranslation();
    const [filas, setFilas] = useState<FilaResumenMsp[]>([]);
    const [campos, setCampos] = useState(() => valoresInicialesEncabezadoMsp());
    const titulo = t(`formularios.${config.claveI18n}.titulo`);
    // 054/055 son de la HCU 2008 (no figuran en la HCU 2021): cita 'HCU 2008…', nunca SNS-MSP/HCU-form.xxx/2021.
    const datosEncabezadoMsp = useDatosEncabezadoMsp(subject, campos, config.codigo, titulo, { citaLegalCompleta: citaHcu2008(config.codigo) });

    const actualizarFila = (i: number, campo: keyof FilaResumenMsp, valor: string): void => {
      setFilas((prev) => prev.map((f, idx) => (idx === i ? { ...f, [campo]: valor } : f)));
    };

    const handleSubmit = (): void => {
      const pares: Par[] = filas
        .filter((f) => f.nombre.trim())
        .map((f) => ({
          pregunta: f.nombre.trim(),
          respuesta: [f.valor.trim(), f.unidad.trim(), f.referencia.trim() ? `(referencia: ${f.referencia.trim()})` : undefined].filter(Boolean).join(' '),
        }));
      onGuardado(pares, datosEncabezadoMsp);
    };

    return (
      <div className="flex flex-col gap-4">
        <h3 className="text-base font-medium text-hce-ink">{titulo}</h3>

        <EncabezadoMspCampos subject={subject} value={campos} onChange={setCampos} />

        <div className="flex flex-col gap-2">
          {filas.map((f, i) => (
            <div key={i} className="flex flex-wrap items-end gap-2">
              <div className="flex flex-1 flex-col gap-1">
                <Label>{t(`formularios.${config.claveI18n}.nombre`)}</Label>
                <Input value={f.nombre} onChange={(e) => actualizarFila(i, 'nombre', e.target.value)} />
              </div>
              <div className="flex flex-1 flex-col gap-1">
                <Label>{t(`formularios.${config.claveI18n}.valor`)}</Label>
                <Input value={f.valor} onChange={(e) => actualizarFila(i, 'valor', e.target.value)} />
              </div>
              <div className="flex flex-1 flex-col gap-1">
                <Label>{t(`formularios.${config.claveI18n}.unidad`)}</Label>
                <Input value={f.unidad} onChange={(e) => actualizarFila(i, 'unidad', e.target.value)} />
              </div>
              <div className="flex flex-1 flex-col gap-1">
                <Label>{t(`formularios.${config.claveI18n}.referencia`)}</Label>
                <Input value={f.referencia} onChange={(e) => actualizarFila(i, 'referencia', e.target.value)} />
              </div>
              <Button type="button" variant="ghost" size="icon" onClick={() => setFilas((prev) => prev.filter((_, idx) => idx !== i))} aria-label={t(`formularios.${config.claveI18n}.quitarFila`)}>
                <Trash2 className="size-4" />
              </Button>
            </div>
          ))}
          <Button type="button" variant="outline" className="w-fit" onClick={() => setFilas((prev) => [...prev, { ...FILA_VACIA }])}>
            {t(`formularios.${config.claveI18n}.agregarFila`)}
          </Button>
        </div>

        <Button type="button" className="w-fit" onClick={handleSubmit}>
          {t('formulario.guardar')}
        </Button>
      </div>
    );
  };
}
